import React, { useRef, useState } from 'react';
import { Upload } from 'lucide-react';

/**
 * Shared click-or-drag file picker. Validation + limit enforcement live in the
 * parent (each upload surface has its own count/limit rules); this component
 * only surfaces the chosen/dropped files via `onFiles(File[])`.
 */
export function FileDropzone({
  onFiles,
  accept,
  multiple = true,
  disabled = false,
  idleLabel = 'Upload files',
  subLabel = '',
  inputTestId,
  dropTestId,
  className = '',
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const emit = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length) onFiles(files);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (disabled) return;
    emit(e.dataTransfer?.files);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      data-testid={dropTestId}
      onClick={() => { if (!disabled) inputRef.current?.click(); }}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragging(true); }}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!disabled) setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }}
      onDrop={onDrop}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed text-sm transition-colors ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
      } ${
        dragging ? 'border-[#1A3E6F] bg-[#EEF4FB]' : 'border-[#CBD5E1] hover:bg-[#F8FAFC]'
      } ${className}`}
    >
      <Upload className="w-4 h-4 text-[#64748B] shrink-0" />
      <span className="text-[#64748B]">
        {dragging ? 'Drop files here' : idleLabel}{subLabel ? ` ${subLabel}` : ''}
      </span>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        data-testid={inputTestId}
        onChange={(e) => { emit(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}

export default FileDropzone;
